import { clearRecoveryEntry, readRecoveryToken } from './recoveryEntry';
import {
  completePasswordReset,
  getPasswordResetStatus,
  inspectPasswordReset,
  RecoveryRequestError,
  RecoveryStatus
} from '../api/passwordRecovery';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCountdown } from '@/shared/hooks/useCountdown';

type RecoveryView = 'completed' | 'form' | 'invalid' | 'loading' | 'missing' | 'processing' | 'support' | 'unconfirmed' | 'unavailable';

interface RecoveryRun {
  controller: AbortController;
  deadline: number;
  deadlineTimer?: number;
  operationId: string;
  pollTimer?: number;
}

interface UsePasswordRecoveryResult {
  inspect: () => Promise<void>;
  isBusy: boolean;
  message: string;
  secondsLeft: number;
  submit: (password: string) => void;
  view: RecoveryView;
}

const CHECK_WINDOW_MS = 120_000;
const POLL_INTERVAL_MS = 2_000;

export const usePasswordRecovery = (): UsePasswordRecoveryResult => {
  const [view, setView] = useState<RecoveryView>('loading');
  const [message, setMessage] = useState('');
  const inspectControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(false);
  const runRef = useRef<RecoveryRun | null>(null);
  const tokenRef = useRef(readRecoveryToken());
  const { secondsLeft, startCountdown } = useCountdown();

  const stopRun = useCallback(() => {
    const run = runRef.current;
    if (!run) return;
    runRef.current = null;
    window.clearTimeout(run.deadlineTimer);
    window.clearTimeout(run.pollTimer);
    run.controller.abort();
  }, []);

  const finishRun = useCallback((run: RecoveryRun, nextView: RecoveryView, nextMessage = '') => {
    if (!isMountedRef.current || runRef.current !== run) return;
    stopRun();
    if (nextView !== 'form') tokenRef.current = '';
    setMessage(nextMessage);
    setView(nextView);
  }, [stopRun]);

  const canApplyResult = useCallback((run: RecoveryRun): boolean => {
    if (!isMountedRef.current || runRef.current !== run || run.controller.signal.aborted) return false;
    if (Date.now() >= run.deadline) {
      finishRun(run, 'unconfirmed');
      return false;
    }
    return true;
  }, [finishRun]);

  const beginRun = useCallback((operationId: string): RecoveryRun => {
    const run: RecoveryRun = {
      controller: new AbortController(),
      deadline: Date.now() + CHECK_WINDOW_MS,
      operationId
    };
    runRef.current = run;
    run.deadlineTimer = window.setTimeout(() => finishRun(run, 'unconfirmed'), CHECK_WINDOW_MS);
    startCountdown(0);
    setMessage('');
    setView('processing');
    return run;
  }, [finishRun, startCountdown]);

  const applyStatus = useCallback((run: RecoveryRun, result: RecoveryStatus): boolean => {
    if (result.status === 'processing') return false;
    if (result.status === 'rejected') {
      finishRun(run, result.can_retry ? 'form' : 'invalid',
        getErrorMessage(new RecoveryRequestError(422, result.reason)));
    } else {
      finishRun(run, result.status === 'completed' ? 'completed' : 'support');
    }
    return true;
  }, [finishRun]);

  const scheduleCheck = useCallback(function scheduleCheck(
    run: RecoveryRun,
    delay = POLL_INTERVAL_MS,
    isDiscovering = false
  ): void {
    if (!canApplyResult(run)) return;
    const wait = Math.max(POLL_INTERVAL_MS, delay);
    if (wait >= run.deadline - Date.now()) return;
    run.pollTimer = window.setTimeout(async () => {
      if (!canApplyResult(run) || !tokenRef.current) return;
      try {
        if (isDiscovering) {
          const result = await inspectPasswordReset({ token: tokenRef.current }, run.controller.signal);
          if (!canApplyResult(run)) return;
          if (result.status === 'operation') {
            run.operationId = result.operation_id;
            scheduleCheck(run);
          } else {
            finishRun(run, 'unconfirmed');
          }
        } else {
          const result = await getPasswordResetStatus({
            operation_id: run.operationId,
            token: tokenRef.current
          }, run.controller.signal);
          if (!canApplyResult(run)) return;
          if (!applyStatus(run, result)) scheduleCheck(run);
        }
      } catch (error) {
        if (!canApplyResult(run)) return;
        if (error instanceof RecoveryRequestError && error.status === 400) {
          finishRun(run, 'invalid', getErrorMessage(error));
        } else if (!(error instanceof RecoveryRequestError)
          || error.status === 0 || error.status === 404 || error.status === 429 || error.status >= 500) {
          scheduleCheck(run, error instanceof RecoveryRequestError ? error.retryAfterSeconds * 1000 : 0, isDiscovering);
        } else {
          finishRun(run, 'unconfirmed');
        }
      }
    }, wait);
  }, [applyStatus, canApplyResult, finishRun]);

  const inspect = useCallback(async () => {
    if (inspectControllerRef.current || runRef.current) return;
    if (!tokenRef.current) {
      setView(tokenRef.current === undefined ? 'missing' : 'invalid');
      return;
    }
    const controller = new AbortController();
    inspectControllerRef.current = controller;
    setMessage('');
    setView('loading');
    startCountdown(0);
    try {
      const result = await inspectPasswordReset({ token: tokenRef.current }, controller.signal);
      if (!isMountedRef.current || inspectControllerRef.current !== controller || controller.signal.aborted) return;
      if (result.status === 'active') {
        setView('form');
      } else {
        inspectControllerRef.current = null;
        scheduleCheck(beginRun(result.operation_id));
      }
    } catch (error) {
      if (!isMountedRef.current || inspectControllerRef.current !== controller || controller.signal.aborted) return;
      setMessage(getErrorMessage(error));
      setView(error instanceof RecoveryRequestError && error.status === 400 ? 'invalid' : 'unavailable');
      if (error instanceof RecoveryRequestError && error.retryAfterSeconds) startCountdown(error.retryAfterSeconds);
    } finally {
      if (inspectControllerRef.current === controller) inspectControllerRef.current = null;
    }
  }, [beginRun, scheduleCheck, startCountdown]);

  const sendAttempt = useCallback(async (run: RecoveryRun, password: string) => {
    if (!tokenRef.current) return;
    try {
      const result = await completePasswordReset({
        new_password: password,
        operation_id: run.operationId,
        token: tokenRef.current
      }, run.controller.signal);
      if (!canApplyResult(run)) return;
      if (!applyStatus(run, result)) scheduleCheck(run);
    } catch (error) {
      if (!canApplyResult(run)) return;
      if (error instanceof RecoveryRequestError && error.status === 422 && error.code === 'password_policy_violation') {
        applyStatus(run, { can_retry: true, reason: 'password_policy_violation', status: 'rejected' });
      } else if (error instanceof RecoveryRequestError && error.status === 400) {
        finishRun(run, 'invalid', getErrorMessage(error));
      } else if (error instanceof RecoveryRequestError && error.status === 409) {
        scheduleCheck(run, POLL_INTERVAL_MS, true);
      } else {
        scheduleCheck(run, error instanceof RecoveryRequestError ? error.retryAfterSeconds * 1000 : 0);
      }
    }
  }, [applyStatus, canApplyResult, finishRun, scheduleCheck]);

  const submit = (password: string): void => {
    if (view !== 'form' || runRef.current || inspectControllerRef.current || !tokenRef.current) return;
    void sendAttempt(beginRun(crypto.randomUUID()), password);
  };

  useEffect(() => {
    isMountedRef.current = true;
    clearRecoveryEntry();
    void inspect();
    return () => {
      isMountedRef.current = false;
      inspectControllerRef.current?.abort();
      inspectControllerRef.current = null;
      stopRun();
      queueMicrotask(() => {
        if (!isMountedRef.current) tokenRef.current = '';
      });
    };
  }, [inspect, stopRun]);

  return { inspect, isBusy: view === 'loading' || view === 'processing', message, secondsLeft, submit, view };
};